/* eslint-disable max-lines */
import { ChangeDetectorRef, Input, OnDestroy, OnInit, ViewChild, Component } from '@angular/core';
import { BehaviorSubject, combineLatest, iif, Observable, of, Subscription, zip } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { FlightType } from '@search-bar/entities/flight-type';
import { SearchType } from '@search-bar/entities/search-type';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { AppFacade } from '@app/app.facade';
import { Airport } from '@app/modules/core/interfaces/airport';
import { SearchBarMobileService } from '@search-bar/services/search-bar-mobile.service';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { ChangeInputPayload } from '@shared/base-search-bar/entities/search-input-payload';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { ToastrTypes } from '@app/entities/toastr';
import { SearchBarRoomComponent } from '@shared/base-search-bar/components/search-bar-room/search-bar-room.component';
import { AMPLITUDE_EVENTS, EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { HotelDetailResponse } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { GuestToPassengerUIPipe } from '@app/pipes/guest-to-paseenger.pipe';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { LocalStorageService } from '@app/services/local-storage.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { AvoService } from '@avo/services/avo.service';
import { isNullOrUndefined } from '@app/util/util';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { hotelDetailEditMapper } from '@packages/packages-result/mappers/package-routing.mapper';
import { hotelDetailRootRoute } from '@hotels/hotel-detail/hotel-detail-routing.module';
import { mapPackageSearchData } from '@packages/packages-search-bar/mappers/packages-url.mapper';
import { packagesResultRootRoute } from '@packages/packages-result/constants/packages-result.routes';
import { EventProperties } from '@avo/interface/trigger-props';
import { TriggerEvent } from '@avo/interface/trigger-entity';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { AutoCompleteDestination, InputDestinationPlace } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { HeaderProductType } from '@header/entities/ds-header-config';
import { homeItemDetailRootRoute } from '@home-item-detail/home-item-detail-routing.module';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyFontSize, TypographyColor, TypographyFontFamily
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-packages-search-bar-base',
  template: '',
  standalone: false
})
export class PackagesSearchBarBaseContainer implements OnInit, OnDestroy {

  @Input() searchType: SearchType;
  @Input() source: EVENT_SOURCE | string = EVENT_SOURCE.TAG;
  @Input() showHotelDetail: boolean = false;
  @Input() hotelDetail: HotelDetailResponse;
  @Input() loadDefaultplaces: boolean;

  @ViewChild('hotelRoomsSelectorRef', { read: SearchBarRoomComponent }) public hotelRoomsSelectorRef: SearchBarRoomComponent;

  public searching: boolean = false;
  public departure: PlaceType = PlaceType.DEPARTURE;
  public arrival: PlaceType = PlaceType.ARRIVAL;
  public isSubmitted: boolean = false;
  public rooms: Room[] = [];
  public roomControl: UntypedFormControl = new UntypedFormControl();
  public countGuests: number = 0;
  public selectedDestinationPlace: boolean;
  public eventPath: BehaviorSubject<EventTarget[]> = new BehaviorSubject<EventTarget[]>([]);
  public currentUrl: string | null = null;

  private subscriptions: Subscription[] = [];
  private childrenAgesInfo: string = 'SEARCH_MODULE.ROOMS.CHILDREN_AGES_INFO';

  protected AvoDispatch = new AvoDispatchOrchestrator();

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  constructor(
    protected domManipulateService: DomManipulateService,
    protected appFacade: AppFacade,
    protected facade: BaseSearchBarFacade,
    protected searchBarMobileService: SearchBarMobileService,
    protected translocoService: TranslocoService,
    protected cdr: ChangeDetectorRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected guestToPassengerUIPipe: GuestToPassengerUIPipe,
    protected localStorageService: LocalStorageService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected mobileDeviceService: MobileDeviceService,
    protected avoService: AvoService,
  ) { }

  ngOnInit(): void {
    if (this.isHome) {
      this.facade.setPackageStep(PackageStep.PACKAGE_HOME);
      this.subscriptions.push(
        this.flightType$.pipe(
          distinctUntilChanged(), filter(flightType => flightType !== FlightType.ROUNDTRIP),
          tap(() => this.selectFlightType(FlightType.ROUNDTRIP))
        ).subscribe());
      this.subscriptions.push(
        this.flightClass$.pipe(
          distinctUntilChanged(), filter(flightClass => flightClass !== FlightClass.CHEAPER),
          tap(() => this.setFlightClass(FlightClass.CHEAPER))
        ).subscribe());
    }
    this.currentUrl = this.router.url;
    if (!this.isResultsPage && !this.showHotelDetail && this.loadDefaultplaces) {
      this.facade.loadSearchData(SearchBy.PACKAGES);
    }
    if (this.isHome) this.registerAmplitudeEventViewTravelSearch();
    this.subscriptions.push(
      this.facade.searchData$.pipe(
        filter(searchData => !!searchData?.guestsPerRoom),
        switchMap(searchData => iif(
          () => !!searchData.guestsPerRoom,
          this.roomInputPackageLabel$(searchData.guestsPerRoom).pipe(
            tap(label => {
              this.setRoomsPackageData(searchData.guestsPerRoom, label);
            })
          ),
          of(null)
        ).pipe(
          tap(() => {
            this.cdr.detectChanges();
            this.cdr.markForCheck();
          })
        ))
      ).subscribe()
    );
    this.subscriptions.push(this.registerViewTravelSearchScreenByEdit());
  }

  ngOnDestroy(): void {
    this.cdr.detach(); this.subscriptions.forEach(s => s.unsubscribe());
  }

  get editSearchIsActivated$(): Observable<boolean> {
    return this.facade.editSearchIsActivated$(HeaderProductType.Packages);
  }

  get isEditButtonVisible$(): Observable<boolean> {
    return this.facade.isHeaderEditButtonVisible$;
  }

  get flightType$(): Observable<FlightType> {
    return this.facade.flightType$;
  }
  get flightClass$(): Observable<FlightClass> {
    return this.facade.flightClass$;
  }
  get guestsPerRoom$(): Observable<Room[]> {
    return this.facade.guestsPerRoom$;
  }
  get isOneWayTrip$(): Observable<boolean> {
    return this.flightType$.pipe(map(flightType => flightType === FlightType.ONEWAY));
  }
  get departurePlace$(): Observable<AutocompletePlace> {
    return this.facade.departurePlace$;
  }
  get destinationPlace$(): Observable<AutocompletePlace> {
    return this.facade.destinationPlace$;
  }
  get packagesDestinationPlace$(): Observable<InputDestinationPlace> {
    return this.facade.packageDestinationPlace$;
  }
  get packagesOriginPlace$(): Observable<InputDestinationPlace> {
    return this.facade.packageOriginPlace$;
  }
  get isLoadingAutocompletePlaces$(): Observable<boolean> {
    return this.facade.isLoadingAutocompletePlaces$;
  }
  get autoCompletePlaces$(): Observable<AutocompletePlaces> {
    return this.facade.autoCompletePlaces$;
  }
  get hotelAutoCompletePlaces$(): Observable<AutoCompleteDestination> {
    return this.facade.autocompleteDestinationPlaces$;
  }
  get airports$(): Observable<Airport[]> {
    return this.appFacade.airports$;
  }
  get departureDate$(): Observable<Date> {
    return this.facade.departureDate$;
  }
  get arrivalDate$(): Observable<Date> {
    return this.facade.arrivalDate$;
  }
  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get isEmptyDeparturePlace$(): Observable<boolean> {
    return this.departurePlace$.pipe(map(departurePlace =>
      this.isEmptyPlace(departurePlace, this.isSubmitted)));
  }

  get isEmptyDestinationPlace$(): Observable<boolean> {
    return this.destinationPlace$.pipe(map(destinationPlace =>
      this.isEmptyPlace(destinationPlace, this.isSubmitted)));
  }

  get activateTooptip$(): Observable<boolean> {
    return combineLatest([this.destinationPlace$, this.departurePlace$]).pipe(map(([destinationPlace, departurePlace]) =>
      !isEmpty(destinationPlace?.code) && !isEmpty(departurePlace?.code)
        ? destinationPlace?.code === departurePlace.code : false));
  }

  get isValidDestinationPlace$(): Observable<boolean> {
    return combineLatest([this.activateTooptip$, this.isEmptyDestinationPlace$])
      .pipe(map(([activateTooptip, isEmptyArrivalPlace]) => activateTooptip || isEmptyArrivalPlace));
  }

  get arrowIcon$(): Observable<Icon> {
    return combineLatest([
      this.isOneWayTrip$, this.isMobile$
    ]).pipe(map(([isOneWayTrip, isMobile]) => this.searchBarMobileService.arrowIcon(isOneWayTrip, isMobile)));
  }

  get isEmptyDepartureDate$(): Observable<boolean> {
    return this.departureDate$.pipe(map(departureDate =>
      this.searchBarMobileService.isEmptyDepartureDate(departureDate, this.isSubmitted)));
  }

  get isEmptyArrivalDate$(): Observable<boolean> {
    return this.arrivalDate$.pipe(map(arrivalDate =>
      this.searchBarMobileService.isEmptyArrivalDate(arrivalDate, this.isSubmitted)));
  }

  public disabledButton$: Observable<boolean> =
    combineLatest([
      this.packagesDestinationPlace$, this.packagesOriginPlace$, this.flightType$, this.flightClass$,
      this.arrivalDate$, this.departureDate$
    ]).pipe(map(([
      packagesDestinationPlace, packagesOriginPlace, flightType, flightClass, arrivalDate, departureDate
    ]) => {
      return isEmpty(packagesDestinationPlace) || isEmpty(packagesOriginPlace) || (
        !isEmpty(packagesDestinationPlace) && !isEmpty(packagesOriginPlace) ? 
          packagesDestinationPlace.NearestIata === packagesOriginPlace.NearestIata : false
      ) || flightType !== FlightType.ROUNDTRIP || flightClass !== FlightClass.CHEAPER || !arrivalDate || !departureDate;
    }));

  get calendarField(): typeof CalendarField {
    return CalendarField;
  }
  get isResultsPage(): boolean {
    return this.searchType === SearchType.PACKAGES_RESULT;
  }
  get passengerValue(): number {
    return this.countGuests ? this.countGuests : 0;
  }
  get passengerLabel(): string {
    return this.passengerValue === 1 ? 'PASSENGER' : 'PASSENGERS';
  }
  get isHome(): boolean {
    return this.searchType === SearchType.PACKAGES_HOME;
  }

  get mappedRooms(): string {
    return this.rooms ? this.rooms.map(item =>
      `${item[TravelerTypes.ADULTS]}_${item[TravelerTypes.CHILDREN]}_${item.childrenAges.join('_')}`).join('-') : null;
  }

  get searchPackagesTrigger$(): Observable<TriggerEvent> {
    return zip(
      this.facade.searchData$, this.disabledButton$,
      this.propertiesAmplitudeService.searchProperties$(SearchBy.PACKAGES).pipe(take(1)),
      iif(
        () => this.showHotelDetail,
        this.route.queryParamMap.pipe(take(1)),
        of(null)
      )
    ).pipe(
      tap(() => {
        this.isSubmitted = true;
      }),
      map(([searchData, disabledButton, searchProps, queryParamMap]) => {
        if (!disabledButton) {
          if (this.showHotelDetail) {
            const packageResultsUrl = this.localStorageService.getDecodePackageBaseUrlResult();
            const urlParams = hotelDetailEditMapper(hotelDetailRootRoute, this.hotelDetail,
              queryParamMap, packageResultsUrl, this.mappedRooms, searchData);
            const isCurrentUrl = this.currentUrl.includes(urlParams);
            return { isCurrentUrl, disabledButton, urlParams, searchProps, searchData };
          } else {
            const urlParams = mapPackageSearchData(searchData);
            const isCurrentUrl = this.currentUrl.replace(
              PackageStep.FLIGHT_RESULTS, PackageStep.HOTEL_RESULTS).includes(urlParams);
            return { isCurrentUrl, disabledButton, urlParams, searchProps, searchData };
          }
        } else {
          return {
            isCurrentUrl: null, disabledButton, urlParams: null, searchProps, searchData
          };
        }
      }),
      switchMap(({ isCurrentUrl, disabledButton, urlParams, searchProps, searchData }) =>
        this.avoService.getEventTriggerProps$(
          AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: this.source }, searchProps
        ).pipe(
          tap(trigger => {
            if (!disabledButton && !isCurrentUrl && urlParams) {
              this.AvoDispatch.selectTravelSearch({
                ...this.avoService.avoBaseProperties(trigger),
                source: this.avoService.getAvoSourceValueType(this.source),
              });
              if (this.showHotelDetail) {
                this.router.navigateByUrl(urlParams);
              } else {
                this.currentUrl = `${packagesResultRootRoute}/${urlParams}`;
                this.router.navigate([`${packagesResultRootRoute}/${urlParams}`], {
                  queryParams: { source: this.source, branch_code: searchData.branchCode }
                });
                this.facade.saveSearchData(searchData, SearchBy.PACKAGES);
                this.facade.resetPackageSelectedOptions();
              }
            }
          })
        )),
    );
  }

  public selectPackagesDeparture(place: InputDestinationPlace): void {
    this.facade.searchData$.pipe(
      take(1),
      tap((searchData) => {
        this.facade.saveSearchData({ ...searchData, packageOriginPlace: place }, SearchBy.PACKAGES);
        this.facade.selectPackageOriginPlace(place);
      })
    ).subscribe().unsubscribe();
  }

  public selectPackagesDestination(place: InputDestinationPlace): void {
    this.facade.searchData$.pipe(
      take(1),
      tap((searchData) => {
        this.facade.saveSearchData({ ...searchData, packageDestinationPlace: place }, SearchBy.PACKAGES);
        this.facade.selectPackageDestinationPlace(place);
      })
    ).subscribe().unsubscribe();
  }

  public swipeAction(): void {
    this.facade.searchData$.pipe(
      take(1),
      tap(searchData => {
        this.selectPackagesDeparture(searchData.packageDestinationPlace);
        this.selectPackagesDestination(searchData.packageOriginPlace);
      })
    ).subscribe().unsubscribe();
  }

  public runAutocomplete(trigger: { q: string, type: PlaceType }): void {
    if (trigger.type === PlaceType.DEPARTURE) {
      this.facade.searchPlaces(trigger.q);
    } else {
      this.facade.searchDestinationPlaces(trigger.q);
    }
  }

  public resetAutocomplete(): void {
    this.facade.resetAutocomplete();
  }

  public roomInputPackageLabel$(rooms: Room[]): Observable<string> {
    return iif(
      () => rooms.length > 1,
      this.translocoService.selectTranslate('HOTELS_MODULE.HOME.ROOMS_SINGLE'),
      this.translocoService.selectTranslate('HOTELS_MODULE.HOME.ROOM')
    ).pipe(
      map(roomsLabel => {
        let countGuests = 0;
        rooms.forEach(data => {
          countGuests += data[TravelerTypes.ADULTS];
          countGuests += data[TravelerTypes.CHILDREN];
        });
        return { roomsLabel: this.toLowerCaseFirstLetter(roomsLabel), countGuests };
      }),
      tap(({ countGuests }) => this.countGuests = countGuests),
      switchMap(({ roomsLabel, countGuests }) => iif(
        () => countGuests > 1,
        this.translocoService.selectTranslate('HOTELS_MODULE.HOME.GUESTS'),
        this.translocoService.selectTranslate('HOTELS_MODULE.HOME.GUEST')
      ).pipe(
        map(guestLabel => `${rooms.length} ${roomsLabel}, ${countGuests} ${this.toLowerCaseFirstLetter(guestLabel)}`)
      ))
    );
  }

  public selectFlightType(event: FlightType): void {
    this.facade.selectFlightType(event);
  }

  public setFlightClass(event: FlightClass): void {
    this.facade.selectFlightClass(event);
  }

  public selectPlace(place: SelectAutocompletePlace, isDestination: boolean): void {
    this.facade.selectPlace(place, isDestination, this.searchType);
    this.facade.searchData$.pipe(
      map(searchData => {
        const search = { ...searchData };
        if (place.type === PlaceType.DEPARTURE) search.departurePlace = place;
        else search.destinationPlace = place;
        return { search };
      }),
      tap(({ search }) => {
        this.facade.saveSearchData(search, SearchBy.PACKAGES);
        this.selectedDestinationPlace = !this.selectedDestinationPlace;
        this.cdr.detectChanges(); this.cdr.markForCheck();
      })
    ).subscribe().unsubscribe();
  }

  public placeInputChanged(input: ChangeInputPayload): void {
    if (input.isEmpty) this.facade.resetAutocomplete();
    else this.facade.searchPlaces(input.value);
  }

  public clearAutocompletePlaces(): void {
    this.facade.resetAutocomplete();
  }

  public roomsPackageSelection(rooms: Room[]): void {
    this.roomInputPackageLabel$(rooms).pipe(
      tap(label => {
        this.setRoomsPackageData(rooms, label);
      })
    ).subscribe().unsubscribe();
  }

  public addRoom(event: Room): void {
    this.guestsPerRoom$.pipe(
      take(1),
      map(rooms => ({ passengers: this.guestToPassengerUIPipe.transform([...rooms, event]) })),
      tap(({ passengers }) => {
        this.setPassengerArrangement(passengers);
        this.facade.addNewRoom(event);
      })
    ).subscribe().unsubscribe();
  }

  public removeRoom(event: number): void {
    this.guestsPerRoom$.pipe(
      take(1),
      map(rooms => ({ passengers: this.guestToPassengerUIPipe.transform(rooms.filter(room => room.id !== event)) })),
      tap(({ passengers }) => {
        this.setPassengerArrangement(passengers);
        this.facade.deleteRoom(event);
      })
    ).subscribe().unsubscribe();
  }

  public setGuestsPerRoom(event: Room): void {
    this.guestsPerRoom$.pipe(
      take(1),
      map(rooms => ({
        passengers: this.guestToPassengerUIPipe.transform([...rooms.slice(0, event.id),
          { ...rooms[event.id], ...event }, ...rooms.slice(event.id + 1)])
      })),
      tap(({ passengers }) => {
        this.setPassengerArrangement(passengers);
        this.facade.setGuestsPerRoom(event);
      })
    ).subscribe().unsubscribe();
  }

  public fetchMessageAction(): void {
    this.appFacade.fetchToastrMessage({
      message: this.translocoService.translate(this.childrenAgesInfo), type: ToastrTypes.ERROR
    });
  }

  public locationNotFoundEvent(props: EventProperties): void {
    this.avoService.getEventTriggerProps$()
      .pipe(
        tap(trigger => {
          this.AvoDispatch.travelLocationNotFound({
            ...this.avoService.avoBaseProperties(trigger),
            searchQuery: props.SEARCH_QUERY as string,
            controlId: props.CONTROL_ID as string,
            travelProductType: props.TRAVEL_PRODUCT_TYPE as string,
          })
        })
      ).subscribe().unsubscribe();
  }

  public popularSelectedEvent(props: EventProperties): void {
    this.amplitudeEvent({ type: AMPLITUDE_EVENTS.SELECT_TRAVEL_POPULAR_DESTINATIONS, props });
  }

  public amplitudeEvent(params: AmplitudeEventParams): void {
    this.triggerAmplitudeService.trackAmplitudeEventProps$(params.type, params.props)
      .pipe(
        tap(trigger => triggerAmplitudeEventApp(trigger))
      ).subscribe().unsubscribe();
  }

  private setRoomsPackageData(rooms: Room[], value: string): void {
    this.rooms = rooms;
    this.roomControl = new UntypedFormControl(value);
  }

  private toLowerCaseFirstLetter(value: string): string {
    return value.charAt(0).toLowerCase() + value.slice(1);
  }

  private setPassengerArrangement(passengers: PassengerArrangement[]): void {
    passengers?.map(passenger => this.facade.setPassengerArrangement(passenger));
  }

  private registerAmplitudeEventViewTravelSearch(): void {
    this.avoService.getEventTriggerProps$(AVO_EVENTS.VIEW_TRAVEL_SEARCH_SCREEN, {
      SOURCE: this.source, TRAVEL_PRODUCT_TYPE: SearchBy.PACKAGES
    }).pipe(
      tap(trigger => {
        this.AvoDispatch.viewTravelSearchScreen({
          ...this.avoService.avoBaseProperties(trigger),
          source: this.avoService.getAvoSourceValueType(this.source),
          travelProductType: SearchBy.PACKAGES
        });
      })
    ).subscribe().unsubscribe();
  }

  private invalidArrivalPlace(arrivalPlace: AutocompletePlace, departurePlace: AutocompletePlace): boolean {
    return isEmpty(arrivalPlace) || (arrivalPlace?.code === departurePlace?.code);
  }

  private isEmptyPlace(place: AutocompletePlace, isSubmitted: boolean): boolean {
    return !!place && isNullOrUndefined(place.code) && isSubmitted;
  }

  private registerViewTravelSearchScreenByEdit(): Subscription {
    return combineLatest([
      this.editSearchIsActivated$, this.isEditButtonVisible$
    ]).pipe(
      switchMap(([editSearchIsActivated, isEditButtonVisible]) => iif(
        () => (
          !!isEditButtonVisible && editSearchIsActivated &&
          (this.isResultsPage || this.currentUrl.indexOf(`/${homeItemDetailRootRoute}/`) >= 0)
        ),
        this.avoService.getEventTriggerProps$(
          AVO_EVENTS.VIEW_TRAVEL_SEARCH_SCREEN, {
            SOURCE: this.source, TRAVEL_PRODUCT_TYPE: SearchBy.PACKAGES
          }).pipe(
          take(1),
          tap(triggerView => {
            this.domManipulateService.resetScrollPosition();
            this.domManipulateService.setBodyScroll(false);
            this.AvoDispatch.viewTravelSearchScreen({
              ...this.avoService.avoBaseProperties(triggerView),
              source: this.avoService.getAvoSourceValueType(this.source),
              travelProductType: SearchBy.PACKAGES
            });
          })
        ),
        of(null)
      ))
    ).subscribe();
  }

}
